/**
 * TeachingVideoUploader — Step for the template wizard.
 *
 * Four input modes (tabs):
 *   - Upload Video (.mp4, .mov, .mkv, .webm, .mpeg — see VIDEO_ACCEPT below;
 *     .m4v is deliberately not offered, FE-6)
 *   - Upload Audio (.m4a, .mp3, .wav, .aac)
 *   - Record Voice Note (the browser's own MediaRecorder → the SAME upload
 *     endpoint; owner decision D2, 2026-10-07 — "support in-browser recording
 *     AND upload". See VoiceNoteRecorder.tsx)
 *   - Paste Notes (textarea)
 *
 * On submit:
 *   1. Upload/paste via the corresponding API call.
 *   2. Immediately trigger processing.
 *   3. Poll via useTeachingVideoResult until completed or failed.
 *   4. On completed: call onComplete(teachingVideoId) so the parent can wire
 *      the result into the Scoring Studio suggestion panel.
 *
 * Product Decision §1: Paid coaches only.
 * Free coaches see an upsell CTA; athlete accounts never see this component
 * (it's only in the coach template wizard).
 *
 * Product Decision §4: Single file — video OR audio, not both.
 * Product Decision §5: Paste-notes is a first-class input method.
 */

import { useState, useRef, useEffect, type ChangeEvent } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Button, Spinner, Alert } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import {
  useUploadTeachingVideo,
  usePasteNotes,
  useTriggerProcessing,
  useTeachingVideoResult,
} from '@/hooks/useTeachingVideo';
import type { User } from '@/types/auth';
import { MAX_TEACHING_VIDEO_DURATION_SECONDS } from '@/utils/constants';
import { validateVideoDuration } from '@/utils/videoValidation';
import { isPaidCoach } from '@/utils/coachAccess';
import VoiceNoteRecorder from './VoiceNoteRecorder';

type InputMode = 'video' | 'audio' | 'record' | 'paste';

interface TeachingVideoUploaderProps {
  user: User | null;
  sport: string;
  movement: string;
  /** Called when processing completes. Receives the teaching video ID. */
  onComplete: (teachingVideoId: string) => void;
  /** Called when user wants to skip this optional step. */
  onSkip?: () => void;
}

// Source of truth for what the backend actually accepts:
// backend/app/api/teaching_video_routes.py's `_ACCEPTED_VIDEO_MIMES` (video)
// and `_ACCEPTED_AUDIO_MIMES` (audio). Keep these extension lists in sync
// with that set — offering an extension the backend rejects produces a 400
// after upload (FE-6). `.m4v` is deliberately OMITTED: its MIME
// (`video/x-m4v`) is not in `_ACCEPTED_VIDEO_MIMES`.
export const VIDEO_ACCEPT = '.mp4,.mov,.mkv,.webm,.mpeg';
const AUDIO_ACCEPT = '.mp3,.m4a,.aac,.wav,.ogg';

function StatusPill({ status }: { status: string }) {
  const map: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
    uploading: { label: 'Uploading...', variant: 'info' },
    processing: { label: 'Processing...', variant: 'warning' },
    uploaded: { label: 'Queued', variant: 'default' },
    completed: { label: 'Complete', variant: 'success' },
    failed: { label: 'Failed', variant: 'error' },
  };
  const entry = map[status] ?? { label: status, variant: 'default' };
  return <Badge variant={entry.variant} size="sm">{entry.label}</Badge>;
}

function PaidUpsellCard() {
  return (
    <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-5 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-warning text-lg">&#9733;</span>
        <p className="font-semibold text-text-primary text-sm">
          Coach Instruction Extraction (Paid Feature)
        </p>
      </div>
      <p className="text-sm text-text-secondary leading-relaxed">
        Upload an audio or video coaching instruction and we will auto-populate
        phases, goals, and cues for your template.
      </p>
      <a
        href="/pricing"
        className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
      >
        Upgrade to access &rarr;
      </a>
    </div>
  );
}

export default function TeachingVideoUploader({
  user,
  sport,
  movement,
  onComplete,
  onSkip,
}: TeachingVideoUploaderProps) {
  const [mode, setMode] = useState<InputMode>('video');
  const [file, setFile] = useState<File | null>(null);
  const [pasteText, setPasteText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [uploadedId, setUploadedId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadTeachingVideo();
  const pasteMutation = usePasteNotes();
  const triggerMutation = useTriggerProcessing();

  const { data: resultData } = useTeachingVideoResult(uploadedId ?? undefined);

  // When polling reaches 'completed', notify parent. This MUST run in an
  // effect, not during render (FE-5): calling `onComplete` — which the
  // parent typically uses to set state — while this component is rendering
  // produces React's "Cannot update a component while rendering a different
  // component" warning/error. The ref guard keeps the "fire once per
  // uploadedId" semantics even if `onComplete`'s identity changes between
  // renders (we intentionally do not add it to the dep array below).
  const currentStatus = resultData?.status;
  const notifiedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentStatus === 'completed' && uploadedId && notifiedIdRef.current !== uploadedId) {
      notifiedIdRef.current = uploadedId;
      onComplete(uploadedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onComplete deliberately excluded; see comment above
  }, [currentStatus, uploadedId]);

  // Paid-coach gate (Product Decision §1). Shared with CreateTemplate.tsx's
  // YouTube-transcript copy branch (FE-10) so the two surfaces cannot drift
  // on who counts as a "paid coach".
  if (!isPaidCoach(user)) {
    return <PaidUpsellCard />;
  }

  const displayStatus = isUploading
    ? 'uploading'
    : (uploadedId ? (resultData?.status ?? 'uploaded') : null);

  const handleModeChange = (newMode: InputMode) => {
    setMode(newMode);
    setFile(null);
    setPasteText('');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);

    try {
      setIsUploading(true);
      let response;

      if (mode === 'paste') {
        if (!pasteText.trim()) {
          setError('Please enter your coaching notes before submitting.');
          setIsUploading(false);
          return;
        }
        response = await pasteMutation.mutateAsync({
          text: pasteText,
          sport,
          movement,
        });
      } else {
        if (!file) {
          setError('Please select a file before uploading.');
          setIsUploading(false);
          return;
        }
        // Duration pre-check for video uploads only.  Audio mode is exempt —
        // cv2 (backend) and the <video> element (browser) both give unreliable
        // duration readings for audio-only containers.
        if (mode === 'video') {
          try {
            await validateVideoDuration(file, MAX_TEACHING_VIDEO_DURATION_SECONDS);
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Video is too long.');
            setIsUploading(false);
            return;
          }
        }
        response = await uploadMutation.mutateAsync({
          file,
          sport,
          movement,
        });
      }

      setIsUploading(false);
      setUploadedId(response.id);

      // Immediately trigger processing so the coach doesn't need a second click
      await triggerMutation.mutateAsync(response.id);
    } catch (err) {
      setIsUploading(false);
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    }
  };

  const isProcessing = displayStatus === 'uploading' || displayStatus === 'processing' || displayStatus === 'uploaded';
  const isCompleted = displayStatus === 'completed';
  const isFailed = displayStatus === 'failed';

  const tabs: { id: InputMode; label: string }[] = [
    { id: 'video', label: 'Upload Video' },
    { id: 'audio', label: 'Upload Audio' },
    { id: 'record', label: 'Record' },
    { id: 'paste', label: 'Paste Notes' },
  ];

  /** The recorder uploads through the same endpoint as the file pickers, so
   *  the only thing left to do is what handleSubmit does after an upload:
   *  remember the id (which starts polling) and kick off processing. */
  const handleRecorded = async (teachingVideoId: string) => {
    setError(null);
    setUploadedId(teachingVideoId);
    try {
      await triggerMutation.mutateAsync(teachingVideoId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'We could not start reading your voice note. Press Try Again.',
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Coach Instruction (optional)</CardTitle>
          {displayStatus && <StatusPill status={displayStatus} />}
        </div>
        <p className="text-sm text-text-secondary mt-1">
          Upload a coaching video, audio clip, or paste your notes. We will
          extract phases, goals, and cues to pre-populate the template.
          Takes 1-2 minutes.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Mode tabs */}
        {!uploadedId && (
          <>
            <div className="flex gap-1 border border-border-subtle rounded-sm-control p-1 bg-dark-elevated-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleModeChange(tab.id)}
                  className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    mode === tab.id
                      ? 'bg-accent text-text-inverse'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Video / Audio file picker */}
            {(mode === 'video' || mode === 'audio') && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">
                  {mode === 'video' ? 'Select a video file' : 'Select an audio file'}
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={mode === 'video' ? VIDEO_ACCEPT : AUDIO_ACCEPT}
                  onChange={handleFileChange}
                  className="block w-full text-sm text-text-secondary
                    file:mr-3 file:py-2 file:px-4
                    file:rounded file:border-0
                    file:text-sm file:font-medium
                    file:bg-dark-elevated-1 file:text-text-primary
                    file:cursor-pointer file:hover:bg-dark-elevated-2
                    cursor-pointer"
                />
                {file && (
                  <p className="text-xs text-text-muted">
                    Selected: {file.name} ({(file.size / (1024 * 1024)).toFixed(1)} MB)
                  </p>
                )}
              </div>
            )}

            {/* Record a voice note (D2) — browser recording, same upload path */}
            {mode === 'record' && (
              <VoiceNoteRecorder
                sport={sport}
                movement={movement}
                onUploaded={handleRecorded}
              />
            )}

            {/* Paste notes textarea */}
            {mode === 'paste' && (
              <div className="space-y-2">
                <label
                  htmlFor="coaching-notes-paste"
                  className="text-sm font-medium text-text-secondary"
                >
                  Paste coaching notes or YouTube transcript
                </label>
                <textarea
                  id="coaching-notes-paste"
                  rows={6}
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder="Paste your coaching script, notes, or a YouTube transcript here. Include cues, phase descriptions, common mistakes, and any angle/timing targets you want scored."
                  className="w-full px-3 py-2 border border-border-subtle rounded-sm-control
                    bg-dark-elevated-2 text-text-primary placeholder-text-muted
                    text-sm resize-y focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                />
                <p className="text-xs text-text-muted">
                  {pasteText.trim().split(/\s+/).filter(Boolean).length} words
                </p>
              </div>
            )}

            {error && <Alert variant="error">{error}</Alert>}

            {/* The recorder carries its own buttons (record / stop / cancel /
                send), so this row is for the two modes that need a submit. */}
            <div className="flex flex-wrap gap-3">
              {mode !== 'record' && (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isUploading || uploadMutation.isPending || pasteMutation.isPending}
                  className="flex-1"
                >
                  {isUploading ? 'Uploading...' : 'Analyze Instruction'}
                </Button>
              )}
              {onSkip && (
                <Button variant="outline" onClick={onSkip}>
                  Skip
                </Button>
              )}
            </div>
          </>
        )}

        {/* Processing / completed state */}
        {uploadedId && (
          <div className="space-y-3">
            {isProcessing && (
              <div className="flex items-center gap-3 py-4">
                <Spinner size="md" />
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    {displayStatus === 'uploading'
                      ? 'Uploading your coaching instruction...'
                      : 'Extracting coaching structure... (1-2 minutes)'}
                  </p>
                  <p className="text-xs text-text-muted">
                    We are transcribing and analysing your instruction with Gemini.
                  </p>
                </div>
              </div>
            )}

            {isCompleted && (
              <Alert variant="success">
                Extraction complete. Review the suggested goals and cues in the
                Scoring Studio panel below.
              </Alert>
            )}

            {isFailed && (
              <div className="space-y-2">
                <Alert variant="error">
                  {resultData?.error_message || 'Extraction failed. Please try again.'}
                </Alert>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setUploadedId(null);
                    setFile(null);
                    setPasteText('');
                    setError(null);
                  }}
                >
                  Try Again
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
